import {
	AlertCircle,
	AlertTriangle,
	Calendar,
	Check,
	CheckCircle2,
	ChevronDown,
	Clock,
	CreditCard,
	FileText,
	Loader2,
	Monitor,
	MapPin,
	Mic,
	PlayCircle,
	Stethoscope,
	Users,
	Video,
	XCircle,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { AuditTimelineItem } from '@/components/audit/AuditTimelineItem';
import { CopyWrapper } from '@/components/ui/CopyWrapper';
import { isSessionTranscriptionAllowed } from '@/constants/sessionCall';
import { cn } from '@/lib/utils';
import { SessionAuditModal } from './SessionAuditModal';
import { useGetSession } from '@/services';
import {
	buildPublicSessionAccessUrl,
	formatProfessionalFullName,
	getSessionDisplayTitle,
	resolveVideoRoomId,
} from '@/utils';
import {
	formatCpfDisplay,
	formatDateDisplay,
	formatPhoneDisplay,
} from '@/utils/participantDetailDisplay';
import { formatDateWithTime } from '@/utils/transformer';

const SESSION_TYPE_LABELS: Record<Sessions.SessionType, string> = {
	DEFAULT: 'Individual',
	RETURN: 'Retorno',
	GROUP: 'Em grupo',
	P2P: 'P2P',
	P2P_TRANSCRIPTION_ONLY: 'P2P transcrição',
};

const SESSION_STATUS_CONFIG: Record<
	Sessions.SessionStatus,
	{ label: string; badge: string; icon: ReactNode }
> = {
	CREATED: {
		label: 'Aguardando pagamento',
		badge: 'bg-amber-50 text-amber-800 ring-amber-200/80',
		icon: <AlertCircle size={14} />,
	},
	CONFIRMED: {
		label: 'Confirmada',
		badge: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
		icon: <CheckCircle2 size={14} />,
	},
	STARTED: {
		label: 'Em andamento',
		badge: 'bg-orange-50 text-orange-800 ring-orange-200/80',
		icon: <PlayCircle size={14} />,
	},
	FINISHED: {
		label: 'Finalizada',
		badge: 'bg-zinc-100 text-zinc-700 ring-zinc-200/80',
		icon: <Check size={14} />,
	},
	CANCELLED: {
		label: 'Cancelada',
		badge: 'bg-red-50 text-red-800 ring-red-200/80',
		icon: <XCircle size={14} />,
	},
};

const PAYMENT_STATUS_LABELS: Record<Sessions.PaymentStatus, string> = {
	WAITING: 'Aguardando',
	DONE: 'Pago',
	FAILED: 'Falhou',
	REFUNDED: 'Reembolsado',
	EXPIRED: 'Expirado',
};

const GATEWAY_LABELS: Record<Sessions.PaymentGateway, string> = {
	STRIPE: 'Stripe',
	ABACATE_PAY: 'Abacate Pay',
	CLUB: 'Plumaa Club',
	MANUAL: 'Manual',
};

const SCHEDULED_BY_LABELS: Record<Sessions.SessionScheduledBy, string> = {
	USER: 'Paciente',
	PROFESSIONAL: 'Profissional',
	ADMIN: 'Admin',
	SUPPORT: 'Suporte',
};

const FINISHED_BY_LABELS: Record<Sessions.SessionFinishedBy, string> = {
	PROFESSIONAL: 'Profissional',
	USER: 'Paciente',
	ADMIN: 'Admin',
	SUPPORT: 'Suporte',
};

const MEETING_STATUS_LABELS: Record<Sessions.VideoMeetingStatus, string> = {
	SCHEDULED: 'Agendada',
	ENDED: 'Encerrada',
	IN_PROGRESS: 'Em andamento',
	CANCELLED: 'Cancelada',
};

const LABEL_CLASS = 'text-[11px] font-medium text-zinc-500';
const VALUE_CLASS = 'mt-0.5 text-sm font-medium leading-snug text-zinc-600';
const VALUE_EMPTY_CLASS = 'font-normal text-zinc-300';
const SECTION_TITLE_CLASS =
	'text-[11px] font-semibold uppercase tracking-wide text-zinc-500';
const COLUMN_DIVIDER_CLASS =
	'hidden lg:block w-px shrink-0 self-stretch bg-zinc-200/70';

const WEEKDAY_LABELS = [
	'Domingo',
	'Segunda',
	'Terça',
	'Quarta',
	'Quinta',
	'Sexta',
	'Sábado',
];

const formatNullableDateTime = (
	value: string | Date | null | undefined,
): string | null => {
	if (!value) return null;
	return formatDateWithTime(value) || null;
};

const parseSemicolonSeparatedMessages = (
	value: string | null | undefined,
): string[] => {
	if (!value?.trim()) return [];

	const seen = new Set<string>();
	const messages: string[] = [];

	for (const part of value.split(';')) {
		const trimmed = part.trim();
		if (!trimmed || seen.has(trimmed)) continue;
		seen.add(trimmed);
		messages.push(trimmed);
	}

	return messages;
};

const collectCalendarIssueMessages = (session: Sessions.Session): string[] => {
	const seen = new Set<string>();
	const messages: string[] = [];

	for (const source of [
		session.mismatch_google_calendar_reason,
		session.scheduling_error,
	]) {
		for (const message of parseSemicolonSeparatedMessages(source)) {
			if (seen.has(message)) continue;
			seen.add(message);
			messages.push(message);
		}
	}

	return messages;
};

const formatPaymentAmount = (amount: string, currency: string) => {
	const numeric = Number.parseFloat(amount);
	if (Number.isNaN(numeric)) return `${amount} ${currency}`;
	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: currency || 'BRL',
	}).format(numeric);
};

const formatMetadata = (
	metadata: Sessions.Session['metadata'],
): string | null => {
	if (metadata == null) return null;
	if (Array.isArray(metadata)) {
		return metadata.length
			? metadata.map((item) => JSON.stringify(item)).join(', ')
			: null;
	}
	return JSON.stringify(metadata, null, 2);
};

const decodeParticipantName = (name: string) => {
	try {
		return decodeURIComponent(name.replace(/\+/g, ' '));
	} catch {
		return name;
	}
};

const getSessionService = (session: Sessions.Session) =>
	session.services ?? null;

const getSessionServiceName = (session: Sessions.Session) =>
	session.service_name ?? session.services?.service_name ?? null;

const InfoRow = ({
	label,
	value,
	mono = false,
	copyValue,
}: {
	label: string;
	value: string | null | undefined;
	mono?: boolean;
	copyValue?: string;
}) => {
	const display = value?.trim() || '—';
	const content = (
		<p
			className={cn(
				VALUE_CLASS,
				mono && 'break-all font-mono text-xs',
				!value?.trim() && VALUE_EMPTY_CLASS,
			)}
		>
			{display}
		</p>
	);

	return (
		<div className="min-w-0">
			<p className={LABEL_CLASS}>{label}</p>
			{copyValue ? (
				<CopyWrapper valueToCopy={copyValue} wrapperClassName="min-w-0">
					<div className="pr-7">{content}</div>
				</CopyWrapper>
			) : (
				content
			)}
		</div>
	);
};

const InfoGrid = ({ children }: { children: ReactNode }) => (
	<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
);

const DetailCollapsible = ({
	title,
	icon,
	badge,
	defaultOpen = false,
	alert = false,
	children,
}: {
	title: string;
	icon?: ReactNode;
	badge?: string | number;
	defaultOpen?: boolean;
	alert?: boolean;
	children: ReactNode;
}) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	return (
		<div
			className={cn(
				'overflow-hidden rounded-2xl border bg-white shadow-sm',
				alert ? 'border-amber-200' : 'border-zinc-200/80',
			)}
		>
			<button
				type="button"
				onClick={() => setIsOpen((prev) => !prev)}
				aria-expanded={isOpen}
				className={cn(
					'flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors md:px-5',
					isOpen ? 'bg-zinc-50' : 'hover:bg-zinc-50/80',
				)}
			>
				{icon ? (
					<span
						className={cn(
							'flex size-8 shrink-0 items-center justify-center rounded-lg',
							alert
								? 'bg-amber-100 text-amber-700'
								: 'bg-zinc-100 text-zinc-600',
						)}
					>
						{icon}
					</span>
				) : null}
				<span className="min-w-0 flex-1 text-sm font-semibold text-zinc-600">
					{title}
				</span>
				{badge != null && badge !== '' ? (
					<span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-500">
						{badge}
					</span>
				) : null}
				<ChevronDown
					size={18}
					className={cn(
						'shrink-0 text-zinc-400 transition-transform duration-200',
						isOpen && 'rotate-180',
					)}
				/>
			</button>
			<div
				className={cn(
					'grid transition-[grid-template-rows] duration-200',
					isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
				)}
			>
				<div className="overflow-hidden">
					<div className="border-t border-zinc-100 px-4 py-4 md:px-5 md:py-5">
						{children}
					</div>
				</div>
			</div>
		</div>
	);
};

const ParticipantChip = ({
	name,
	phone,
	subtitle,
}: {
	name: string;
	phone?: string | null;
	subtitle?: string | null;
}) => (
	<div className="flex items-center gap-3 rounded-xl border border-zinc-100/80 bg-zinc-50/50 px-3 py-2.5">
		<div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-sm font-semibold text-zinc-600">
			{name.charAt(0).toUpperCase()}
		</div>
		<div className="min-w-0">
			<p className="truncate text-sm font-medium text-zinc-600">{name}</p>
			{phone ? (
				<p className="text-xs text-zinc-500">{phone}</p>
			) : subtitle ? (
				<p className="text-xs text-zinc-500">{subtitle}</p>
			) : null}
		</div>
	</div>
);

const TimelineItem = ({
	label,
	value,
	isLast = false,
}: {
	label: string;
	value: string | null | undefined;
	isLast?: boolean;
}) => (
	<div className={cn('relative flex gap-3 pb-4', isLast && 'pb-0')}>
		{!isLast ? (
			<span className="absolute left-[5px] top-3 h-[calc(100%-4px)] w-px bg-zinc-200" />
		) : null}
		<span
			className={cn(
				'relative z-[1] mt-1.5 size-2.5 shrink-0 rounded-full ring-2 ring-white',
				value ? 'bg-emerald-400' : 'bg-zinc-200',
			)}
		/>
		<div className="min-w-0 flex-1">
			<p className={LABEL_CLASS}>{label}</p>
			<p
				className={cn(
					'text-sm font-medium text-zinc-600',
					!value?.trim() && VALUE_EMPTY_CLASS,
				)}
			>
				{value?.trim() || '—'}
			</p>
		</div>
	</div>
);

const SessionAuditPreviewColumn = ({
	auditLogs,
	hasMore,
	onViewAll,
}: {
	auditLogs: AuditLogs.AuditLogResponse[] | undefined;
	hasMore: boolean | undefined;
	onViewAll: () => void;
}) => {
	const entries = auditLogs ?? [];

	return (
		<div className="min-w-0">
			<p className={cn(SECTION_TITLE_CLASS, 'mb-3')}>Auditoria</p>
			{entries.length === 0 ? (
				<p className="text-sm text-zinc-400">
					Nenhum evento registrado
				</p>
			) : (
				<div>
					{entries.map((entry, index) => (
						<AuditTimelineItem
							key={entry.id}
							entry={entry}
							isLast={index === entries.length - 1 && !hasMore}
							compact
						/>
					))}
				</div>
			)}
			{hasMore ? (
				<button
					type="button"
					onClick={onViewAll}
					className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-600 underline decoration-zinc-300 underline-offset-2 transition-colors hover:text-zinc-900"
				>
					<FileText size={13} strokeWidth={2.25} aria-hidden />
					Ver todos os eventos
				</button>
			) : null}
		</div>
	);
};

const PaymentCard = ({
	payment,
	index,
}: {
	payment: Sessions.PaymentDetails;
	index: number;
}) => {
	const statusStyles: Record<Sessions.PaymentStatus, string> = {
		WAITING: 'bg-amber-50 text-amber-700',
		DONE: 'bg-emerald-50 text-emerald-700',
		FAILED: 'bg-red-50 text-red-700',
		REFUNDED: 'bg-zinc-100 text-zinc-600',
		EXPIRED: 'bg-orange-50 text-orange-700',
	};

	return (
		<div className="rounded-xl border border-zinc-100 bg-zinc-50/60 p-3.5">
			<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
				<span className="text-xs font-semibold text-zinc-500">
					Pagamento {index + 1}
				</span>
				<span
					className={cn(
						'rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
						statusStyles[payment.status],
					)}
				>
					{PAYMENT_STATUS_LABELS[payment.status]}
				</span>
			</div>
			<InfoGrid>
				<InfoRow
					label="Valor"
					value={formatPaymentAmount(
						payment.amount,
						payment.currency,
					)}
				/>
				<InfoRow
					label="Gateway"
					value={GATEWAY_LABELS[payment.gateway] ?? payment.gateway}
				/>
				<InfoRow
					label="Pago em"
					value={formatNullableDateTime(payment.payment_done_at)}
				/>
				<InfoRow
					label="ID"
					value={payment.id}
					mono
					copyValue={payment.id}
				/>
				{payment.receipt_url ? (
					<InfoRow
						label="Recibo"
						value={payment.receipt_url}
						mono
						copyValue={payment.receipt_url}
					/>
				) : null}
				{payment.payment_benefit_application ? (
					<InfoRow
						label="Benefício"
						value={`Desconto ${
							payment.payment_benefit_application
								.amount_discounted ?? '—'
						}`}
					/>
				) : null}
				{payment.gateway_mismatch ? (
					<InfoRow
						label="Divergência"
						value={payment.gateway_mismatch_reason ?? 'Sim'}
					/>
				) : null}
			</InfoGrid>
		</div>
	);
};

const VideoRoomBlock = ({
	room,
	index,
}: {
	room: Sessions.SessionVideoRoom;
	index: number;
}) => {
	const events = room.participant_events ?? [];

	return (
		<div className="space-y-3 rounded-xl border border-zinc-100 bg-zinc-50/60 p-3.5">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-xs font-bold text-zinc-500">
					Sala {index + 1}
				</span>
				{room.meeting_status ? (
					<span className="rounded-md bg-zinc-200/80 px-2 py-0.5 text-[10px] font-bold uppercase text-zinc-600">
						{MEETING_STATUS_LABELS[room.meeting_status] ??
							room.meeting_status}
					</span>
				) : null}
			</div>
			<InfoGrid>
				<InfoRow label="Provedor" value={room.provider} />
				<InfoRow label="Ativa" value={room.is_active ? 'Sim' : 'Não'} />
				<InfoRow
					label="Room ID"
					value={room.room_id}
					mono
					copyValue={room.room_id}
				/>
				<InfoRow
					label="URL"
					value={room.room_url}
					mono
					copyValue={room.room_url}
				/>
			</InfoGrid>
			{events.length > 0 ? (
				<div className="space-y-2 border-t border-zinc-200/80 pt-3">
					<p className={cn(SECTION_TITLE_CLASS, 'normal-case')}>
						Eventos na sala
					</p>
					{events.map((event) => (
						<div
							key={event.id}
							className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-xs"
						>
							<span className="font-medium text-zinc-600">
								{decodeParticipantName(event.participant_name)}
							</span>
							<span className="text-zinc-500">
								{event.entity_type} ·{' '}
								{event.event_type === 'JOINED'
									? 'Entrou'
									: 'Saiu'}{' '}
								· {formatNullableDateTime(event.occurred_at)}
							</span>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
};

interface AdminSessionDetailModalProps {
	session: Sessions.Session;
}

export const AdminSessionDetailModal = ({
	session,
}: AdminSessionDetailModalProps) => {
	const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);

	const {
		data: fetchedSession,
		isLoading,
		isError,
	} = useGetSession(session.id, {
		staleTime: 0,
		refetchOnMount: 'always',
	});

	const activeSession = fetchedSession ?? session;

	const vm = useMemo(() => {
		const startDate = new Date(activeSession.selected_date);
		const endDate = new Date(startDate.getTime() + 50 * 60000);
		const hasValidDate = !Number.isNaN(startDate.getTime());

		const formattedDate = hasValidDate
			? startDate.toLocaleDateString('pt-BR', {
					weekday: 'long',
					day: 'numeric',
					month: 'long',
					year: 'numeric',
			  })
			: null;

		const timeRange = hasValidDate
			? `${startDate.toLocaleTimeString('pt-BR', {
					hour: '2-digit',
					minute: '2-digit',
			  })} – ${endDate.toLocaleTimeString('pt-BR', {
					hour: '2-digit',
					minute: '2-digit',
			  })}`
			: null;

		const videoRooms = activeSession.video_rooms?.length
			? activeSession.video_rooms
			: activeSession.video_room
			  ? [activeSession.video_room]
			  : [];

		const primaryRoom = videoRooms[0] ?? null;
		const roomId = primaryRoom ? resolveVideoRoomId(primaryRoom) : null;
		const accessUrl = roomId ? buildPublicSessionAccessUrl(roomId) : null;

		const participants = activeSession.participants ?? [];
		const isGroup =
			activeSession.type === 'GROUP' || participants.length > 1;
		const isP2P =
			activeSession.type === 'P2P' ||
			activeSession.type === 'P2P_TRANSCRIPTION_ONLY';
		const payments = activeSession.payments ?? [];
		const service = getSessionService(activeSession);

		const hasCalendarIssue =
			!!activeSession.scheduling_error?.trim() ||
			activeSession.mismatch_google_calendar === true;

		const showTranscriptionSection =
			(activeSession.is_online ||
				activeSession.type === 'P2P_TRANSCRIPTION_ONLY') &&
			(activeSession.type === 'P2P' ||
				activeSession.type === 'P2P_TRANSCRIPTION_ONLY' ||
				activeSession.type === 'DEFAULT') &&
			(typeof activeSession.transcription_allowed === 'boolean' ||
				activeSession.transcription_mock === true ||
				activeSession.transcription_started === true ||
				activeSession.claude_usage_recorded_at != null ||
				activeSession.daily_co_usage_recorded_at != null);

		const notes = [
			{ label: 'Profissional', value: activeSession.professional_notes },
			{
				label: 'Profissional (privado)',
				value: activeSession.professional_private_notes,
			},
			{ label: 'Paciente', value: activeSession.user_notes },
			{
				label: 'Paciente (privado)',
				value: activeSession.user_private_notes,
			},
		].filter((n) => n.value?.trim());

		const metadataFormatted = formatMetadata(activeSession.metadata);

		const hasTechnicalData =
			!!metadataFormatted ||
			!!activeSession.guests?.length ||
			!!activeSession.searchable?.trim() ||
			!!activeSession.available_date_id;

		return {
			title: getSessionDisplayTitle(activeSession),
			statusConfig:
				SESSION_STATUS_CONFIG[activeSession.status] ??
				SESSION_STATUS_CONFIG.CREATED,
			formattedDate,
			timeRange,
			videoRooms,
			accessUrl,
			participants,
			isGroup,
			isP2P,
			payments,
			service,
			serviceName: getSessionServiceName(activeSession),
			hasCalendarIssue,
			showTranscriptionSection,
			notes,
			metadataFormatted,
			hasTechnicalData,
		};
	}, [activeSession]);

	if (isLoading && !fetchedSession) {
		return (
			<div className="flex min-h-[320px] items-center justify-center">
				<Loader2 className="size-7 animate-spin text-zinc-300" />
			</div>
		);
	}

	if (isError && !fetchedSession) {
		return (
			<p className="py-8 text-center text-sm text-red-600">
				Não foi possível carregar os detalhes da sessão.
			</p>
		);
	}

	const user = activeSession.user ?? vm.participants[0]?.user;
	const patient = vm.participants[0]?.patient;
	const primaryName =
		patient?.full_name?.trim() ||
		(user
			? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
			: `${activeSession.first_name ?? ''} ${
					activeSession.last_name ?? ''
			  }`.trim()) ||
		null;
	const primaryEmail = patient?.email ?? user?.email ?? null;
	const primaryParticipant = vm.participants[0];
	const participantUserId =
		activeSession.user_id ?? primaryParticipant?.user_id ?? null;
	const participantPatientId =
		primaryParticipant?.patient_id ?? patient?.id ?? null;
	const participantEntityId = participantUserId
		? { label: 'ID usuário', value: participantUserId }
		: participantPatientId
		  ? { label: 'ID paciente', value: participantPatientId }
		  : null;

	return (
		<div className="space-y-3">
			<div className="rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-5 text-white shadow-lg md:p-6">
				<div className="flex flex-wrap items-start justify-between gap-4">
					<div className="min-w-0 space-y-3">
						<div className="flex flex-wrap items-center gap-2">
							<span
								className={cn(
									'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset',
									vm.statusConfig.badge,
								)}
							>
								{vm.statusConfig.icon}
								{vm.statusConfig.label}
							</span>
							<span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80">
								{SESSION_TYPE_LABELS[activeSession.type]}
							</span>
							<span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/80">
								{activeSession.is_online ? (
									<>
										<Monitor size={12} />
										Online
									</>
								) : (
									<>
										<MapPin size={12} />
										Presencial
									</>
								)}
							</span>
						</div>
						<div>
							<p className="text-lg font-bold leading-tight md:text-xl">
								{vm.title}
							</p>
							{vm.formattedDate ? (
								<p className="mt-1 text-sm capitalize text-white/60">
									{vm.formattedDate}
									{vm.timeRange ? ` · ${vm.timeRange}` : ''}
								</p>
							) : null}
						</div>
					</div>
					<div className="w-full shrink-0 sm:w-auto sm:max-w-xs">
						<p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
							ID da sessão
						</p>
						<CopyWrapper
							valueToCopy={activeSession.id}
							wrapperClassName="min-w-0"
						>
							<p className="truncate pr-8 font-mono text-xs text-white/70">
								{activeSession.id}
							</p>
						</CopyWrapper>
					</div>
				</div>
			</div>

			{vm.hasCalendarIssue ? (
				<div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
					<AlertTriangle className="mt-0.5 size-4 shrink-0" />
					<div className="min-w-0 space-y-1">
						<p className="font-semibold">
							Problema no Google Calendar
						</p>
						{collectCalendarIssueMessages(activeSession).map(
							(message) => (
								<p
									key={message}
									className="text-xs leading-relaxed opacity-90"
								>
									{message}
								</p>
							),
						)}
						{activeSession.mismatch_google_calendar_at ? (
							<p className="text-[11px] opacity-70">
								Detectado em{' '}
								{formatNullableDateTime(
									activeSession.mismatch_google_calendar_at,
								)}
							</p>
						) : null}
					</div>
				</div>
			) : null}

			<DetailCollapsible
				title="Pessoas e serviço"
				icon={<Users size={16} />}
				defaultOpen
			>
				<div className="flex flex-col gap-5 lg:flex-row lg:items-stretch">
					<div className="min-w-0 flex-1 space-y-3">
						<p className={SECTION_TITLE_CLASS}>Profissional</p>
						<ParticipantChip
							name={formatProfessionalFullName(
								activeSession.professional,
							)}
							subtitle={activeSession.professional.email}
						/>
						<InfoRow
							label="Telefone"
							value={formatPhoneDisplay(
								activeSession.professional.phone_number,
							)}
						/>
						<InfoRow
							label="ID"
							value={activeSession.professional.id}
							mono
							copyValue={activeSession.professional.id}
						/>
					</div>

					<div aria-hidden className={COLUMN_DIVIDER_CLASS} />

					<div className="min-w-0 flex-1 space-y-3">
						<p className={SECTION_TITLE_CLASS}>
							{vm.isGroup ? 'Participantes' : 'Paciente'}
						</p>
						{vm.isGroup ? (
							vm.participants.length ? (
								<div className="space-y-2">
									{vm.participants.map(
										(participant, index) => {
											const name =
												participant.patient?.full_name?.trim() ||
												`${
													participant.user
														?.first_name ?? ''
												} ${
													participant.user
														?.last_name ?? ''
												}`.trim() ||
												`Participante ${index + 1}`;
											const email =
												participant.patient?.email ??
												participant.user?.email ??
												null;
											return (
												<ParticipantChip
													key={
														participant.id ?? index
													}
													name={name}
													subtitle={
														email ??
														`${
															participant.entity_type
														}${
															participant.presence
																? ` · ${participant.presence}`
																: ''
														}`
													}
												/>
											);
										},
									)}
								</div>
							) : (
								<p className="text-sm text-zinc-500">
									Nenhum participante
								</p>
							)
						) : (
							<>
								<ParticipantChip
									name={primaryName ?? '—'}
									subtitle={primaryEmail}
								/>
								<div className="grid grid-cols-2 gap-3">
									<InfoRow
										label="CPF"
										value={formatCpfDisplay(patient?.cpf)}
									/>
									<InfoRow
										label="Telefone"
										value={formatPhoneDisplay(
											patient?.phone ??
												user?.phone_number,
										)}
									/>
									{participantEntityId ? (
										<InfoRow
											label={participantEntityId.label}
											value={participantEntityId.value}
											mono
											copyValue={
												participantEntityId.value
											}
										/>
									) : null}
								</div>
							</>
						)}
					</div>

					<div aria-hidden className={COLUMN_DIVIDER_CLASS} />

					<div className="min-w-0 flex-1 space-y-3">
						<p className={SECTION_TITLE_CLASS}>Serviço</p>
						<div className="grid gap-3">
							<InfoRow label="Nome" value={vm.serviceName} />
							{vm.service ? (
								<InfoRow
									label="Valor"
									value={formatPaymentAmount(
										vm.service.value,
										vm.service.value_currency,
									)}
								/>
							) : null}
							<InfoRow
								label="Agendado por"
								value={
									activeSession.scheduled_by
										? SCHEDULED_BY_LABELS[
												activeSession.scheduled_by
										  ]
										: null
								}
							/>
							{activeSession.type === 'GROUP' ? (
								<InfoRow
									label="Máx. participantes"
									value={String(
										activeSession.max_participants ?? '—',
									)}
								/>
							) : null}
							{activeSession.parent_session ? (
								<InfoRow
									label="Sessão pai"
									value={activeSession.parent_session}
									mono
									copyValue={activeSession.parent_session}
								/>
							) : null}
							{typeof activeSession.included_in_club ===
							'boolean' ? (
								<InfoRow
									label="Clube"
									value={
										activeSession.included_in_club
											? 'Incluída'
											: 'Não incluída'
									}
								/>
							) : null}
						</div>
					</div>
				</div>
			</DetailCollapsible>

			<DetailCollapsible
				title="Linha do tempo"
				icon={<Clock size={16} />}
				defaultOpen
			>
				<div className="space-y-6">
					<div className="grid gap-6 lg:grid-cols-2">
						<div className="min-w-0">
							<p className={cn(SECTION_TITLE_CLASS, 'mb-3')}>
								Marcos
							</p>
							<TimelineItem
								label="Criada"
								value={formatNullableDateTime(
									activeSession.created_at,
								)}
							/>
							{!vm.isP2P ? (
								<TimelineItem
									label="Paga"
									value={formatNullableDateTime(
										activeSession.payment_done_at,
									)}
								/>
							) : null}
							<TimelineItem
								label="Iniciada"
								value={formatNullableDateTime(
									activeSession.started_at,
								)}
							/>
							<TimelineItem
								label="Finalizada"
								value={formatNullableDateTime(
									activeSession.finished_at,
								)}
							/>
							<TimelineItem
								label="Atualizada"
								value={formatNullableDateTime(
									activeSession.updated_at,
								)}
								isLast
							/>
						</div>
						<SessionAuditPreviewColumn
							auditLogs={activeSession.audit_logs}
							hasMore={activeSession.audit_logs_has_more_than_20}
							onViewAll={() => setIsAuditModalOpen(true)}
						/>
					</div>
					<InfoGrid>
						{activeSession.finished_by ? (
							<InfoRow
								label="Encerrada por"
								value={
									FINISHED_BY_LABELS[
										activeSession.finished_by
									]
								}
							/>
						) : null}
						{activeSession.status_description ? (
							<InfoRow
								label="Descrição do status"
								value={activeSession.status_description}
							/>
						) : null}
						{activeSession.available_date ? (
							<InfoRow
								label="Dia da semana"
								value={
									WEEKDAY_LABELS[
										activeSession.available_date.week_day
									] ??
									String(
										activeSession.available_date.week_day,
									)
								}
							/>
						) : null}
						{activeSession.available_date ? (
							<InfoRow
								label="Fuso horário"
								value={activeSession.available_date.timezone}
							/>
						) : null}
					</InfoGrid>
				</div>
			</DetailCollapsible>

			{!vm.isP2P && vm.payments.length > 0 ? (
				<DetailCollapsible
					title="Pagamentos"
					icon={<CreditCard size={16} />}
					badge={vm.payments.length}
					defaultOpen
				>
					<div className="space-y-3">
						{vm.payments.map((payment, index) => (
							<PaymentCard
								key={payment.id}
								payment={payment}
								index={index}
							/>
						))}
					</div>
				</DetailCollapsible>
			) : null}

			{vm.isP2P ? (
				<DetailCollapsible
					title="Pagamentos"
					icon={<CreditCard size={16} />}
				>
					<p className="text-sm text-zinc-500">
						Sessões P2P não possuem pagamento vinculado à
						plataforma.
					</p>
				</DetailCollapsible>
			) : null}

			{vm.videoRooms.length > 0 || vm.accessUrl ? (
				<DetailCollapsible
					title="Vídeo"
					icon={<Video size={16} />}
					badge={vm.videoRooms.length || undefined}
					defaultOpen={vm.videoRooms.some(
						(r) => (r.participant_events?.length ?? 0) > 0,
					)}
				>
					<div className="space-y-3">
						{vm.accessUrl ? (
							<InfoRow
								label="Link público de acesso"
								value={vm.accessUrl}
								mono
								copyValue={vm.accessUrl}
							/>
						) : null}
						{vm.videoRooms.map((room, index) => (
							<VideoRoomBlock
								key={room.id}
								room={room}
								index={index}
							/>
						))}
					</div>
				</DetailCollapsible>
			) : null}

			{(activeSession.meets_url ||
				activeSession.googleCalendarEventId) && (
				<DetailCollapsible
					title="Integrações"
					icon={<Calendar size={16} />}
				>
					<InfoGrid>
						{activeSession.meets_url ? (
							<InfoRow
								label="Google Meet"
								value={activeSession.meets_url}
								mono
								copyValue={activeSession.meets_url}
							/>
						) : null}
						{activeSession.googleCalendarEventId ? (
							<InfoRow
								label="Google Calendar Event ID"
								value={activeSession.googleCalendarEventId}
								mono
								copyValue={activeSession.googleCalendarEventId}
							/>
						) : null}
					</InfoGrid>
				</DetailCollapsible>
			)}

			{vm.showTranscriptionSection ? (
				<DetailCollapsible
					title="Transcrição e uso de IA"
					icon={<Mic size={16} />}
					defaultOpen={activeSession.transcription_started === true}
				>
					<div className="space-y-3">
						<div
							className={cn(
								'rounded-xl px-3 py-2.5 text-xs',
								isSessionTranscriptionAllowed(activeSession)
									? 'bg-emerald-50 text-emerald-800'
									: 'bg-zinc-50 text-zinc-600',
							)}
						>
							<p className="font-semibold">
								{isSessionTranscriptionAllowed(activeSession)
									? 'Transcrição habilitada'
									: 'Transcrição desabilitada'}
							</p>
							{activeSession.transcription_mock ? (
								<p className="mt-0.5 opacity-80">
									Modo mock ativo
								</p>
							) : null}
						</div>
						<InfoGrid>
							<InfoRow
								label="Transcrição iniciada"
								value={
									activeSession.transcription_started != null
										? activeSession.transcription_started
											? 'Sim'
											: 'Não'
										: null
								}
							/>
							<InfoRow
								label="Uso Claude registrado"
								value={formatNullableDateTime(
									activeSession.claude_usage_recorded_at,
								)}
							/>
							<InfoRow
								label="Uso Daily.co registrado"
								value={formatNullableDateTime(
									activeSession.daily_co_usage_recorded_at,
								)}
							/>
						</InfoGrid>
						{activeSession.medical_record_session ? (
							<div className="space-y-2 border-t border-zinc-100 pt-3">
								<p className={SECTION_TITLE_CLASS}>
									Prontuário
								</p>
								<InfoGrid>
									<InfoRow
										label="Sessão nº"
										value={String(
											activeSession.medical_record_session
												.session_number,
										)}
									/>
									{activeSession.medical_record_session
										.short_summary ? (
										<InfoRow
											label="Resumo"
											value={
												activeSession
													.medical_record_session
													.short_summary
											}
										/>
									) : null}
								</InfoGrid>
							</div>
						) : null}
					</div>
				</DetailCollapsible>
			) : null}

			{vm.notes.length > 0 ? (
				<DetailCollapsible
					title="Notas"
					icon={<Stethoscope size={16} />}
					badge={vm.notes.length}
				>
					<div className="space-y-3">
						{vm.notes.map((note) => (
							<div
								key={note.label}
								className="rounded-xl bg-zinc-50 px-3 py-2.5"
							>
								<p className={LABEL_CLASS}>{note.label}</p>
								<p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-zinc-600">
									{note.value}
								</p>
							</div>
						))}
					</div>
				</DetailCollapsible>
			) : null}

			{vm.hasTechnicalData ? (
				<DetailCollapsible
					title="Dados técnicos"
					icon={<AlertCircle size={16} />}
				>
					<InfoGrid>
						<InfoRow
							label="ID do serviço"
							value={activeSession.service_id}
							mono
							copyValue={activeSession.service_id}
						/>
						{activeSession.available_date_id ? (
							<InfoRow
								label="ID horário"
								value={activeSession.available_date_id}
								mono
								copyValue={activeSession.available_date_id}
							/>
						) : null}
						<InfoRow
							label="Data agendada"
							value={formatDateDisplay(
								activeSession.selected_date,
							)}
						/>
						{activeSession.searchable?.trim() ? (
							<InfoRow
								label="Índice de busca"
								value={activeSession.searchable.trim()}
								mono
								copyValue={activeSession.searchable.trim()}
							/>
						) : null}
					</InfoGrid>
					{activeSession.guests?.length ? (
						<div className="mt-4 flex flex-wrap gap-2">
							{activeSession.guests.map((guest) => (
								<span
									key={guest.id}
									className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-600"
								>
									{`${guest.first_name} ${guest.last_name}`.trim()}
								</span>
							))}
						</div>
					) : null}
					{vm.metadataFormatted ? (
						<pre className="mt-4 max-h-40 overflow-auto rounded-xl bg-zinc-900 p-3 text-[11px] leading-relaxed text-zinc-300">
							{vm.metadataFormatted}
						</pre>
					) : null}
				</DetailCollapsible>
			) : null}
			<SessionAuditModal
				isOpen={isAuditModalOpen}
				onClose={() => setIsAuditModalOpen(false)}
				session={activeSession}
			/>
		</div>
	);
};
